import {
  AFFECTED_OBJECT_SUGGESTION,
  CHANGE_CATEGORY,
  CONDITION_OPERATOR,
  PREFILL_KIND,
  SAFETY_CONCERN,
  SCOPE_TYPE,
  SYSTEM_CHANGE_FIELD_KEY,
  TASK_PRIORITY,
  TASK_TYPE,
  TAssessmentSectionConfig,
  TAssessmentTemplateConfig,
  TEMPLATE_FIELD_TYPE,
} from '../models';

// Built-in, fully config-driven definition of the "Supplier or component change"
// assessment template (prompt Part 4). Nothing here is hardcoded inside a page
// component: sections, custom fields, questions, options, prefills, suggestions,
// candidate affected objects and preliminary activities all live in this config,
// so another tenant can change any of it. This is the source for newly created
// Changes; an accepted Change stores an immutable snapshot instead.
//
// A tenant's own definition (organization.mock.changeV2.assessmentTemplates)
// replaces this built-in fallback at runtime.

// Section ids referenced by custom fields, questions and conditions.
const S = {
  supplierQualification: 'sec-supplier-qualification',
  equivalence: 'sec-component-equivalence',
  biological: 'sec-biological-evaluation',
  risk: 'sec-risk-management',
  verification: 'sec-verification-validation',
  regulatory: 'sec-regulatory-udi',
} as const;

export const SUPPLIER_COMPONENT_CHANGE_TEMPLATE: TAssessmentTemplateConfig = {
  id: 'supplier-component-change',
  version: 6,
  name: 'Supplier or component change',
  description:
    'Qualify a replacement component or supplier and assess the resulting product, quality and regulatory impacts.',
  requestPrefills: {
    [SYSTEM_CHANGE_FIELD_KEY.TITLE]: 'Supplier and component change – Patient-contacting sensor patch',
    [SYSTEM_CHANGE_FIELD_KEY.SCOPE]: SCOPE_TYPE.QMS_AND_TD,
    [SYSTEM_CHANGE_FIELD_KEY.CHANGE_CATEGORY]: CHANGE_CATEGORY.MAINTENANCE,
    [SYSTEM_CHANGE_FIELD_KEY.CRITICALITY]: 'Medium',
  },

  // -- Layer 2: template custom fields (organization-configured structured facts).
  customFields: [
    {
      id: 'currentSupplier',
      label: 'Current supplier',
      fieldType: TEMPLATE_FIELD_TYPE.SHORT_TEXT,
      sectionId: S.supplierQualification,
      required: false,
      prefillKind: PREFILL_KIND.REQUEST_FACT,
      prefilledValue: '{{input:currentSupplier}}',
    },
    {
      id: 'newSupplier',
      label: 'New supplier',
      fieldType: TEMPLATE_FIELD_TYPE.SHORT_TEXT,
      sectionId: S.supplierQualification,
      required: true,
      prefillKind: PREFILL_KIND.REQUEST_FACT,
      prefilledValue: '{{input:newSupplier}}',
    },
    {
      id: 'supplierChangeReason',
      label: 'Reason for supplier change',
      fieldType: TEMPLATE_FIELD_TYPE.LONG_TEXT,
      sectionId: S.supplierQualification,
      required: false,
      prefillKind: PREFILL_KIND.REQUEST_FACT,
      prefilledValue: '{{input:changeReason}}',
    },
    {
      id: 'supplierQualificationStatus',
      label: 'Supplier qualification status',
      fieldType: TEMPLATE_FIELD_TYPE.SINGLE_CHOICE,
      sectionId: S.supplierQualification,
      required: false,
      options: ['Not started', 'In progress', 'Complete'],
      prefillKind: PREFILL_KIND.TEMPLATE_DEFAULT,
      prefilledValue: 'Not started',
    },
    {
      id: 'cutoverStrategy',
      label: 'Planned cutover',
      helpText: 'Suggested cutover approach; confirm or adjust for this change.',
      fieldType: TEMPLATE_FIELD_TYPE.LONG_TEXT,
      sectionId: S.supplierQualification,
      required: false,
      prefillKind: PREFILL_KIND.TEMPLATE_SUGGESTION,
      prefilledValue: 'Use approved {{input:currentComponent}} stock, then introduce {{input:newComponent}}',
    },
    {
      id: 'currentComponent',
      label: 'Current component',
      fieldType: TEMPLATE_FIELD_TYPE.SHORT_TEXT,
      sectionId: S.equivalence,
      required: false,
      prefillKind: PREFILL_KIND.REQUEST_FACT,
      prefilledValue: '{{input:currentComponent}}',
    },
    {
      id: 'newComponent',
      label: 'New component',
      fieldType: TEMPLATE_FIELD_TYPE.SHORT_TEXT,
      sectionId: S.equivalence,
      required: true,
      prefillKind: PREFILL_KIND.REQUEST_FACT,
      prefilledValue: '{{input:newComponent}}',
    },
    {
      id: 'componentFunction',
      label: 'Component function',
      fieldType: TEMPLATE_FIELD_TYPE.SHORT_TEXT,
      sectionId: S.equivalence,
      required: false,
      prefillKind: PREFILL_KIND.NONE,
    },
    {
      id: 'patientContact',
      label: 'Patient contact',
      fieldType: TEMPLATE_FIELD_TYPE.YES_NO_UNKNOWN,
      sectionId: S.biological,
      required: false,
      prefillKind: PREFILL_KIND.NONE,
    },
  ],

  // -- Layer 3: six configured assessment sections with their questions.
  assessmentSections: [
    {
      id: S.supplierQualification,
      title: 'Supplier qualification',
      description: 'Confirm the new supplier can be qualified and the required agreements are in place.',
      responsibleFunction: 'Supplier quality',
      customFieldIds: [
        'currentSupplier',
        'newSupplier',
        'supplierChangeReason',
        'supplierQualificationStatus',
        'cutoverStrategy',
      ],
      questions: [
        {
          id: 'supplierAlreadyApproved',
          label: 'Is {{input:newSupplier}} already an approved supplier?',
          controlType: TEMPLATE_FIELD_TYPE.YES_NO_UNKNOWN,
          required: true,
          prefillKind: PREFILL_KIND.TEMPLATE_SUGGESTION,
          prefilledValue: 'No',
        },
        {
          id: 'technicalPackageAvailable',
          label: 'Is the required supplier and component documentation available?',
          controlType: TEMPLATE_FIELD_TYPE.SINGLE_CHOICE,
          required: true,
          options: ['Complete', 'Partial', 'No', 'Unknown'],
          prefillKind: PREFILL_KIND.TEMPLATE_SUGGESTION,
          prefilledValue: 'Partial',
        },
        {
          id: 'qualityAgreementRequired',
          label: 'Is a supplier quality agreement required?',
          controlType: TEMPLATE_FIELD_TYPE.SINGLE_CHOICE,
          required: true,
          options: ['Yes', 'No', 'To be determined'],
          prefillKind: PREFILL_KIND.TEMPLATE_SUGGESTION,
          prefilledValue: 'Yes',
        },
      ],
    },
    {
      id: S.equivalence,
      title: 'Component and material equivalence',
      description: 'Assess whether the replacement component and its materials remain equivalent.',
      responsibleFunction: 'Engineering or R&D',
      customFieldIds: ['currentComponent', 'newComponent', 'componentFunction'],
      questions: [
        {
          id: 'componentFunctionUnchanged',
          label: 'Is the intended component function unchanged?',
          controlType: TEMPLATE_FIELD_TYPE.YES_NO_UNKNOWN,
          required: true,
          prefillKind: PREFILL_KIND.TEMPLATE_SUGGESTION,
          prefilledValue: 'Yes',
        },
        {
          id: 'electricalInterfaceUnchanged',
          label: 'Are the electrode positions and electrical interface unchanged?',
          controlType: TEMPLATE_FIELD_TYPE.YES_NO_UNKNOWN,
          required: true,
          prefillKind: PREFILL_KIND.TEMPLATE_SUGGESTION,
          prefilledValue: 'Yes',
        },
        {
          id: 'physicalDifferences',
          label: 'What physical characteristics are changing?',
          controlType: TEMPLATE_FIELD_TYPE.MULTI_SELECT,
          required: false,
          allowsComment: true,
          options: [
            'Backing outline',
            'Orientation mark',
            'Dimensions or tolerances',
            'Connector or interface',
            'Other',
          ],
          prefillKind: PREFILL_KIND.TEMPLATE_DEFAULT,
          prefilledValue: ['Backing outline', 'Orientation mark'],
        },
        {
          id: 'materialCompositionChanged',
          label: 'Is the material composition changing?',
          controlType: TEMPLATE_FIELD_TYPE.YES_NO_UNKNOWN,
          required: true,
          allowsComment: true,
          prefillKind: PREFILL_KIND.TEMPLATE_SUGGESTION,
          prefilledValue: 'Yes',
          prefilledComment: 'Revised patient-contacting medical-grade acrylic adhesive',
        },
      ],
    },
    {
      id: S.biological,
      title: 'Biological evaluation impact',
      description: 'Determine the biological-evaluation impact of the changed patient-contacting material.',
      responsibleFunction: 'Biological safety',
      customFieldIds: ['patientContact'],
      questions: [
        {
          id: 'patientContactingMaterialChanged',
          label: 'Is a patient-contacting material changing?',
          controlType: TEMPLATE_FIELD_TYPE.YES_NO_UNKNOWN,
          required: true,
          prefillKind: PREFILL_KIND.TEMPLATE_SUGGESTION,
          prefilledValue: 'Yes',
          bindsToCustomFieldId: 'patientContact',
        },
        {
          id: 'contactTypeOrDurationChanged',
          label: 'Is the nature or maximum duration of body contact changing?',
          controlType: TEMPLATE_FIELD_TYPE.YES_NO_UNKNOWN,
          required: true,
          prefillKind: PREFILL_KIND.TEMPLATE_SUGGESTION,
          prefilledValue: 'No',
        },
        {
          id: 'existingBiologicalEvidenceSufficient',
          label: 'Is the existing biological evidence sufficient for the revised adhesive?',
          helpText: 'Do not pre-decide whether additional testing is legally required.',
          controlType: TEMPLATE_FIELD_TYPE.SINGLE_CHOICE,
          required: true,
          options: ['Yes', 'No', 'To be determined'],
          prefillKind: PREFILL_KIND.TEMPLATE_DEFAULT,
          defaultValue: 'To be determined',
        },
      ],
    },
    {
      id: S.risk,
      title: 'Risk management impact',
      description: 'Identify whether the risk management file needs updating and which areas to review.',
      responsibleFunction: 'Engineering or R&D',
      customFieldIds: [],
      questions: [
        {
          id: 'riskManagementUpdateRequired',
          label: 'Does the risk management file require an update?',
          controlType: TEMPLATE_FIELD_TYPE.SINGLE_CHOICE,
          required: true,
          options: ['Yes', 'No', 'To be determined'],
          prefillKind: PREFILL_KIND.TEMPLATE_SUGGESTION,
          prefilledValue: 'Yes',
        },
        {
          id: 'potentialRiskAreas',
          label: 'Which potential risk areas require review?',
          controlType: TEMPLATE_FIELD_TYPE.MULTI_SELECT,
          required: false,
          options: [
            'Patch detachment',
            'Skin interaction',
            'Removal behaviour',
            'Incorrect placement',
            'Reduced signal quality',
          ],
          prefillKind: PREFILL_KIND.TEMPLATE_SUGGESTION,
          prefilledValue: [
            'Patch detachment',
            'Skin interaction',
            'Removal behaviour',
            'Incorrect placement',
            'Reduced signal quality',
          ],
        },
      ],
    },
    {
      id: S.verification,
      title: 'Verification and validation',
      description: 'Define the comparative verification and any process validation required.',
      responsibleFunction: 'Engineering or R&D',
      customFieldIds: [],
      questions: [
        {
          id: 'comparativeVerificationRequired',
          label: 'Is comparative verification required?',
          controlType: TEMPLATE_FIELD_TYPE.SINGLE_CHOICE,
          required: true,
          options: ['Yes', 'No', 'To be determined'],
          prefillKind: PREFILL_KIND.TEMPLATE_SUGGESTION,
          prefilledValue: 'Yes',
        },
        {
          id: 'verificationScope',
          label: 'What should the comparison cover?',
          controlType: TEMPLATE_FIELD_TYPE.MULTI_SELECT,
          required: false,
          options: ['Retention', 'Removal behaviour', 'Placement', 'Signal quality', 'Interface compatibility'],
          prefillKind: PREFILL_KIND.TEMPLATE_SUGGESTION,
          prefilledValue: ['Retention', 'Removal behaviour', 'Placement', 'Signal quality', 'Interface compatibility'],
        },
        {
          id: 'processValidationRequired',
          label: 'Is process validation or revalidation required?',
          controlType: TEMPLATE_FIELD_TYPE.SINGLE_CHOICE,
          required: true,
          options: ['Yes', 'No', 'To be determined'],
          prefillKind: PREFILL_KIND.TEMPLATE_DEFAULT,
          defaultValue: 'To be determined',
        },
      ],
    },
    {
      id: S.regulatory,
      title: 'Regulatory and UDI impact',
      description: 'Assess the regulatory, classification and UDI impact. Never prefill a regulatory conclusion.',
      responsibleFunction: 'Regulatory affairs',
      customFieldIds: [],
      questions: [
        {
          id: 'intendedPurposeChanged',
          label: 'Is the intended purpose changing?',
          controlType: TEMPLATE_FIELD_TYPE.YES_NO_UNKNOWN,
          required: true,
          prefillKind: PREFILL_KIND.TEMPLATE_SUGGESTION,
          prefilledValue: 'No',
        },
        {
          id: 'classificationImpact',
          label: 'Is a change to device classification expected?',
          controlType: TEMPLATE_FIELD_TYPE.SINGLE_CHOICE,
          required: true,
          options: ['Yes', 'No', 'To be determined'],
          prefillKind: PREFILL_KIND.TEMPLATE_SUGGESTION,
          prefilledValue: 'No',
          confirmationNote: 'Requires Regulatory Affairs confirmation',
        },
        {
          id: 'udiImpact',
          label: 'Is UDI or device-registration data affected?',
          controlType: TEMPLATE_FIELD_TYPE.SINGLE_CHOICE,
          required: true,
          options: ['Yes', 'No', 'To be determined'],
          prefillKind: PREFILL_KIND.TEMPLATE_DEFAULT,
          defaultValue: 'To be determined',
        },
        {
          id: 'externalNotificationImpact',
          label: 'Is notification or approval by a notified body, certification body or authority required?',
          controlType: TEMPLATE_FIELD_TYPE.SINGLE_CHOICE,
          required: true,
          options: ['Yes', 'No', 'To be determined'],
          prefillKind: PREFILL_KIND.TEMPLATE_DEFAULT,
          defaultValue: 'To be determined',
        },
      ],
    },
  ],

  // -- Candidate affected objects (Level 1 candidate impacts, all reviewable).
  candidateAffectedObjects: [
    {
      id: 'cao-supplier-qa',
      label: 'Supplier qualification and quality agreement',
      initialSuggestion: AFFECTED_OBJECT_SUGGESTION.LIKELY_AFFECTED,
      responsibleFunction: 'Supplier quality',
      suggestedBy: 'Template',
    },
    {
      id: 'cao-component-spec',
      label: 'Component specification',
      initialSuggestion: AFFECTED_OBJECT_SUGGESTION.LIKELY_AFFECTED,
      responsibleFunction: 'Engineering or R&D',
      suggestedBy: 'Template',
    },
    {
      id: 'cao-bom',
      label: 'Bill of materials',
      initialSuggestion: AFFECTED_OBJECT_SUGGESTION.LIKELY_AFFECTED,
      responsibleFunction: 'Engineering or R&D',
      suggestedBy: 'Template',
    },
    {
      id: 'cao-device-description',
      label: 'Device description',
      initialSuggestion: AFFECTED_OBJECT_SUGGESTION.LIKELY_AFFECTED,
      responsibleFunction: 'Engineering or R&D',
      suggestedBy: 'Selected products',
    },
    {
      id: 'cao-risk-file',
      label: 'Risk management file',
      initialSuggestion: AFFECTED_OBJECT_SUGGESTION.LIKELY_AFFECTED,
      responsibleFunction: 'Engineering or R&D',
      suggestedBy: 'Template',
    },
    {
      id: 'cao-biological-evaluation',
      label: 'Biological evaluation',
      initialSuggestion: AFFECTED_OBJECT_SUGGESTION.REVIEW_REQUIRED,
      responsibleFunction: 'Biological safety',
      suggestedBy: 'Assessment answers',
    },
    {
      id: 'cao-verification',
      label: 'Verification protocol and report',
      initialSuggestion: AFFECTED_OBJECT_SUGGESTION.LIKELY_AFFECTED,
      responsibleFunction: 'Engineering or R&D',
      suggestedBy: 'Template',
    },
    {
      id: 'cao-incoming-inspection',
      label: 'Incoming inspection specification',
      initialSuggestion: AFFECTED_OBJECT_SUGGESTION.REVIEW_REQUIRED,
      responsibleFunction: 'Production or operations',
      suggestedBy: 'Relationships',
    },
    {
      id: 'cao-manufacturing',
      label: 'Manufacturing instructions',
      initialSuggestion: AFFECTED_OBJECT_SUGGESTION.REVIEW_REQUIRED,
      responsibleFunction: 'Production or operations',
      suggestedBy: 'Relationships',
    },
    {
      id: 'cao-ifu-labeling',
      label: 'IFU and labeling',
      initialSuggestion: AFFECTED_OBJECT_SUGGESTION.REVIEW_REQUIRED,
      responsibleFunction: 'Regulatory affairs',
      suggestedBy: 'Template',
    },
    {
      id: 'cao-udi',
      label: 'UDI and registration data',
      initialSuggestion: AFFECTED_OBJECT_SUGGESTION.REVIEW_REQUIRED,
      responsibleFunction: 'Regulatory affairs',
      suggestedBy: 'Metadata',
    },
    {
      id: 'cao-market-records',
      label: 'Regulatory market records',
      initialSuggestion: AFFECTED_OBJECT_SUGGESTION.REVIEW_REQUIRED,
      responsibleFunction: 'Regulatory affairs',
      suggestedBy: 'Metadata',
    },
  ],

  // -- Preliminary implementation suggestions (become draft planning items after approval).
  preliminaryActivities: [
    {
      id: 'pa-1',
      title: 'Qualify {{input:newSupplier}} and approve the quality agreement',
      description:
        'Complete supplier qualification for {{input:newSupplier}} and approve the applicable quality agreement for {{input:newComponent}}.',
      responsibleFunction: 'Supplier quality',
      owner: 'Dr. Anna Keller',
      taskType: TASK_TYPE.SUPPLIER_QUALIFICATION,
      priority: TASK_PRIORITY.HIGH,
      dueInDays: 10,
      completionCriteria: 'The new supplier is approved to supply {{input:newComponent}}.',
      linkedObject: 'Supplier qualification and quality agreement',
    },
    {
      id: 'pa-2',
      title:
        'Assess {{input:newComponent}} against {{input:currentComponent}} and update the Device Description and Specification for {{input:affectedProduct}}',
      description:
        'Compare component specifications, materials and interfaces, then update the controlled device description and specification.',
      responsibleFunction: 'Engineering or R&D',
      owner: 'Lukas Poterpin',
      taskType: TASK_TYPE.DOCUMENT_REVISION,
      priority: TASK_PRIORITY.HIGH,
      dueInDays: 15,
      completionCriteria:
        'The component comparison and specification update are reviewed and accepted without unsupported safety, performance or equivalence conclusions.',
      linkedObject: 'TD40014 Device Description and Specification',
      documents: [
        {
          _id: '6a4a475a1f350133d5fb33af',
          name: 'Device Description & Specification',
        },
      ],
    },
    {
      id: 'pa-3',
      title: 'Update the risk management file for {{input:newComponent}}',
      description:
        'Add the change-specific assessment of retention, skin interaction, removal behaviour, placement and signal quality.',
      responsibleFunction: 'Engineering or R&D',
      owner: 'Lukas Poterpin',
      taskType: TASK_TYPE.RISK_MANAGEMENT,
      priority: TASK_PRIORITY.HIGH,
      dueInDays: 21,
      completionCriteria:
        'The risk owner has reviewed and accepted the change-specific risks, controls, verification references and residual-risk conclusion.',
      dependencies: ['pa-2'],
      linkedObject: 'TD40110 Risk Management File',
    },
    {
      id: 'pa-4',
      title: 'Assess and update the biological evaluation where required',
      description:
        'Assess the revised patient-contacting material and determine whether additional biological evidence or testing is needed.',
      responsibleFunction: 'Biological safety',
      owner: 'Dr. Sofia Meier',
      taskType: TASK_TYPE.BIOLOGICAL_EVALUATION,
      priority: TASK_PRIORITY.NORMAL,
      dueInDays: 21,
      completionCriteria:
        'The revised material is evaluated and any need for additional evidence or testing is formally determined and approved.',
      dependencies: ['pa-2'],
      linkedObject: 'BIO-CNX-001 Biological Evaluation',
    },
    {
      id: 'pa-5',
      title: 'Prepare and execute comparative verification for {{input:currentComponent}} and {{input:newComponent}}',
      description:
        'Define approved acceptance criteria and verify compatibility, retention, removal behaviour, placement and signal quality.',
      responsibleFunction: 'Engineering or R&D',
      owner: 'Lukas Poterpin',
      taskType: TASK_TYPE.VERIFICATION_VALIDATION,
      priority: TASK_PRIORITY.HIGH,
      dueInDays: 35,
      completionCriteria:
        'Compatibility, retention, removal behaviour, placement and signal-quality requirements are met against approved acceptance criteria.',
      dependencies: ['pa-2', 'pa-3', 'pa-4'],
      linkedObject: 'VP-SP-200 and VR-SP-200',
    },
    {
      id: 'pa-6',
      title: 'Complete the IFU, UDI and regulatory impact review',
      description:
        'Confirm the user-information, usability, UDI, notified-body and implementation-readiness impacts of the change.',
      responsibleFunction: 'Regulatory affairs',
      owner: 'Elena Rossi',
      taskType: TASK_TYPE.REGULATORY_ASSESSMENT,
      priority: TASK_PRIORITY.HIGH,
      dueInDays: 42,
      completionCriteria:
        'Required user-information changes are implemented or justified as unnecessary, regulatory impacts are formally assessed and implementation is authorised.',
      dependencies: ['pa-1', 'pa-2', 'pa-3', 'pa-4', 'pa-5'],
      linkedObject: 'IFU impact decision and RA-CA-042',
    },
  ],

  suggestedAssessorFunctions: [
    'Supplier quality',
    'Engineering or R&D',
    'Quality assurance',
    'Regulatory affairs',
    'Biological safety',
    'Production or operations',
  ],
};

// Platform-level conditional sections added to every assessment in addition to the
// template sections (prompt Part 2). Their visibility depends on Change facts and
// on assessment answers; none of them preselects a regulatory or CAPA decision.
export const PLATFORM_CONDITIONAL_SECTIONS: TAssessmentSectionConfig[] = [
  {
    id: 'platform-safety-reportability',
    title: 'Safety and reportability review',
    description: 'Route safety and reportability questions to PRRC or vigilance. Reportability is never preselected.',
    responsibleFunction: 'PRRC or vigilance',
    platformSection: 'safetyReportability',
    // Visible when a known safety concern is Yes or Unknown, or an answer triggers review.
    visibleWhen: {
      combinator: 'or',
      conditions: [
        {
          sourceKey: SYSTEM_CHANGE_FIELD_KEY.KNOWN_SAFETY_CONCERN,
          operator: CONDITION_OPERATOR.EQUALS,
          value: SAFETY_CONCERN.YES,
        },
        {
          sourceKey: SYSTEM_CHANGE_FIELD_KEY.KNOWN_SAFETY_CONCERN,
          operator: CONDITION_OPERATOR.EQUALS,
          value: SAFETY_CONCERN.UNKNOWN,
        },
        { sourceKey: 'distributedProductsAffected', operator: CONDITION_OPERATOR.EQUALS, value: 'Yes' },
      ],
    },
    customFieldIds: [],
    questions: [
      {
        id: 'distributedProductsAffected',
        label: 'May existing distributed products be affected?',
        controlType: TEMPLATE_FIELD_TYPE.YES_NO_UNKNOWN,
        required: true,
        prefillKind: PREFILL_KIND.NONE,
      },
      {
        id: 'safetyReviewRoutedToPrrc',
        label: 'Should this be routed to PRRC or vigilance for review?',
        controlType: TEMPLATE_FIELD_TYPE.YES_NO_UNKNOWN,
        required: true,
        prefillKind: PREFILL_KIND.NONE,
      },
      {
        id: 'reportabilityAssessment',
        label: 'Reportability assessment',
        helpText: 'Reportability is a human decision. Do not preselect a reportability conclusion.',
        controlType: TEMPLATE_FIELD_TYPE.SINGLE_CHOICE,
        required: true,
        options: ['To be determined', 'Not reportable', 'Potentially reportable'],
        prefillKind: PREFILL_KIND.TEMPLATE_DEFAULT,
        defaultValue: 'To be determined',
      },
    ],
  },
  {
    id: 'platform-capa-determination',
    title: 'CAPA determination',
    description: 'Determine whether a CAPA is required. A linked CAPA reuses the same underlying actions later.',
    responsibleFunction: 'Quality assurance',
    platformSection: 'capaDetermination',
    // Visible only for a nonconformity or potential nonconformity.
    visibleWhen: {
      sourceKey: SYSTEM_CHANGE_FIELD_KEY.CHANGE_CATEGORY,
      operator: CONDITION_OPERATOR.EQUALS,
      value: CHANGE_CATEGORY.NONCONFORMITY,
    },
    customFieldIds: [],
    questions: [
      {
        id: 'capaRequired',
        label: 'CAPA required',
        controlType: TEMPLATE_FIELD_TYPE.SINGLE_CHOICE,
        required: true,
        options: ['Yes', 'No', 'To be determined'],
        prefillKind: PREFILL_KIND.TEMPLATE_DEFAULT,
        defaultValue: 'To be determined',
      },
      {
        id: 'capaRationale',
        label: 'Rationale',
        controlType: TEMPLATE_FIELD_TYPE.LONG_TEXT,
        required: false,
        prefillKind: PREFILL_KIND.NONE,
      },
      {
        id: 'capaImmediateContainment',
        label: 'Immediate containment',
        controlType: TEMPLATE_FIELD_TYPE.SHORT_TEXT,
        required: false,
        prefillKind: PREFILL_KIND.NONE,
      },
      {
        id: 'capaRootCauseRequired',
        label: 'Root-cause investigation required',
        controlType: TEMPLATE_FIELD_TYPE.YES_NO_UNKNOWN,
        required: false,
        prefillKind: PREFILL_KIND.NONE,
      },
      {
        id: 'capaEffectivenessReviewRequired',
        label: 'Effectiveness review required',
        controlType: TEMPLATE_FIELD_TYPE.YES_NO_UNKNOWN,
        required: false,
        prefillKind: PREFILL_KIND.NONE,
      },
    ],
  },
  {
    id: 'platform-immediate-containment',
    title: 'Immediate containment status',
    description: 'Track containment work identified in the request.',
    responsibleFunction: 'Quality assurance',
    platformSection: 'immediateContainment',
    // Visibility for this section is driven by whether the request created containment
    // work (a Change-level fact), handled in the visibility selector.
    customFieldIds: [],
    questions: [
      {
        id: 'containmentStatus',
        label: 'Containment status',
        controlType: TEMPLATE_FIELD_TYPE.SINGLE_CHOICE,
        required: true,
        options: ['Not started', 'In progress', 'Complete'],
        prefillKind: PREFILL_KIND.NONE,
      },
      {
        id: 'containmentEffective',
        label: 'Is the containment effective so far?',
        controlType: TEMPLATE_FIELD_TYPE.YES_NO_UNKNOWN,
        required: false,
        prefillKind: PREFILL_KIND.NONE,
      },
    ],
  },
];

export const MOCK_ASSESSMENT_TEMPLATES: TAssessmentTemplateConfig[] = [SUPPLIER_COMPONENT_CHANGE_TEMPLATE];

export const getAssessmentTemplateById = (
  templates: TAssessmentTemplateConfig[],
  id?: string
): TAssessmentTemplateConfig | undefined => (id ? templates.find((template) => template.id === id) : undefined);
